/**
 * The shared vocabulary of the roster engine (PLAN.md §4).
 *
 * This module is the bottom of the dependency graph: `src/data/` imports from
 * `src/engine/`, never the reverse, and `Minutes` is declared here exactly once
 * so that no second, subtly different definition of "a time" can appear.
 *
 * The invariant that must not break: a time in this app is always ACT
 * wall-clock, expressed as minutes on *some named date's* clock. Nothing here
 * is an instant, nothing here carries a timezone, and nothing here is ever
 * converted to one — which is what makes DST structurally unable to enter the
 * arithmetic.
 */

/**
 * A civil date as `'YYYY-MM-DD'`.
 *
 * Deliberately a string rather than a branded type or a `Date`: it sorts
 * lexicographically in the same order it sorts chronologically, it survives a
 * round trip through JSON and Postgres unchanged, and it cannot accidentally
 * acquire a time-of-day or a timezone on the way.
 */
export type IsoDate = string

/**
 * Minutes since midnight, 0–1439, on the *start* date's clock.
 *
 * Every duration and every rest gap in this app is an integer number of these.
 * See `absoluteMinutes` in `./civil` for the single integer space they are
 * compared in.
 */
export type Minutes = number

/** Minutes in a civil day. Always 1440 here, because nothing is an instant. */
export const MINUTES_PER_DAY = 1440

/** The four ACTAS shifts: AM 0630–1630, D 0900–2100, PM 1100–2300, N 2100–0700. */
export type ShiftCode = 'AM' | 'D' | 'PM' | 'N'

/** One shift's times, as the roster defines them. */
export interface RosterShift {
  code: ShiftCode
  /** Minutes since midnight on the start date, e.g. 390 for 06:30. */
  startMin: Minutes
  /**
   * Minutes since midnight on the *start* date's clock, so the night shift's
   * 07:00 (420) is less than its 21:00 start (1260). Crossing midnight is
   * derived from `endMin <= startMin` rather than stored as a flag, because a
   * stored flag is a second source of truth that can disagree with the times.
   */
  endMin: Minutes
}

/**
 * The eight roster blocks. Each is offset one day from the last within the
 * 8-day `AM → D → PM → N → off ×4` cycle, so on any date exactly four blocks
 * are working — one on each shift — and four are off.
 */
export type BlockCode = '1A' | '1B' | '2A' | '2B' | '3A' | '3B' | '4A' | '4B'

/** Which day a calendar week starts on. Both are in use in Australia. */
export type WeekStart = 'SUNDAY' | 'MONDAY'

/** Why an explicit shift row exists, once one does. */
export type ShiftOrigin =
  /** A derived block shift that needed a database identity — advertised or swapped. */
  | 'BLOCK_MATERIALISED'
  /** A flexible-working entry the user added by hand. */
  | 'FWA_MANUAL'
  /** A flexible-working entry generated from a recurrence rule. */
  | 'FWA_RECURRING'
  /** A shift taken on from someone else through a confirmed swap. */
  | 'SWAP_ACQUIRED'

/**
 * A shift as the roster sees it, before it has any database identity.
 *
 * A block user's roster is not stored — it is computed from their block — so
 * most `ShiftInstance`s are `'DERIVED'` and carry no `shiftId` at all. A row
 * appears only at the moment the shift needs an identity, which is when it is
 * advertised or swapped.
 */
export interface ShiftInstance {
  date: IsoDate
  code: ShiftCode
  source: 'DERIVED' | 'EXPLICIT'
  /** Set only once the shift has been materialised into a database row — Phase 4. */
  shiftId?: string
  /** Set only on an explicit shift; a purely derived one has no origin to record. */
  origin?: ShiftOrigin
}

/**
 * A day on which the block pattern does not apply to this user.
 *
 * Subtractive only: an exception suppresses a derived shift, it never creates
 * one. `NOT_WORKING` covers a day the block says is working but the user is
 * not, without claiming a reason.
 *
 * `id` and `note` exist for the UI, not the merge: the id is what `DaySheet`
 * needs to delete the row (Phase 3's `day_exceptions` table gives every row
 * one), and the note is the free-text sentence shown beside the day. Both are
 * optional because the engine's callers may build exceptions by hand — the
 * Phase 1 fixtures do — and `effectiveRoster` ignores both entirely: it keys
 * on `date` and `kind` and nothing else.
 */
export interface DayException {
  /** The `day_exceptions` row's uuid, once the exception has one. */
  id?: string
  date: IsoDate
  kind: 'LEAVE' | 'UNAVAILABLE' | 'NOT_WORKING'
  /** Free text shown with the day; never read by the merge. */
  note?: string
}

/** A shift that exists as a database row, with a lifecycle of its own. */
export interface ExplicitShift {
  id: string
  date: IsoDate
  code: ShiftCode
  origin: ShiftOrigin
  status: 'ROSTERED' | 'ADVERTISED' | 'PENDING_SWAP' | 'SWAPPED_AWAY' | 'CANCELLED'
}

/**
 * A closed interval over whole days — **both ends inclusive**.
 *
 * Inclusive because every range in this app is something a human named ("this
 * month", "the next four weeks"), and a half-open end date is the kind of
 * off-by-one that shows up as a missing shift on the last day of a calendar.
 */
export interface DateRange {
  from: IsoDate
  to: IsoDate
}

/**
 * The roster as configuration rather than constants, so that a change to the
 * anchor or the shift times is a fixture update and not a code change.
 *
 * Built once in `src/data/roster-config.ts`; the engine takes it as an
 * argument and holds no roster constants of its own.
 */
export interface RosterConfig {
  /** The date block `blockOrder[0]` starts its AM — Tue 26/05/2026. */
  anchorDate: IsoDate
  /** The blocks in anchor order; a block's position here is its `blockIndex`. */
  blockOrder: readonly BlockCode[]
  /** Days in the cycle. 8 for the ACTAS pattern. */
  cycleLength: number
  /** Indexed by cycle day; `null` means off. */
  cycleDayShifts: readonly (ShiftCode | null)[]
  /** Times for each `ShiftCode`. */
  shifts: readonly RosterShift[]
  /** How far either side of today the roster is generated. */
  horizon: { monthsBack: number; monthsForward: number }
}
